import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Trash2, Copy, Lightbulb, Image as ImageIcon, Linkedin, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { relativeTime } from "@/lib/utils";

const TONES = ["thought_leadership", "contrarian", "data_driven", "storytelling"];

export default function ContentStudioPage() {
  const [topic, setTopic] = useState("Why drayage capacity is the #1 lever for ocean freight margins in 2026");
  const [tone, setTone] = useState("thought_leadership");
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestMeta, setSuggestMeta] = useState(null);
  const [autoImage, setAutoImage] = useState(false);

  const load = () => api.get("/content/drafts").then((r) => setDrafts(r.data.drafts || []));
  useEffect(() => { load(); }, []);

  const suggestTopics = async () => {
    setSuggestBusy(true);
    try {
      const { data } = await api.get("/content/topic-suggestions");
      setSuggestions(data.suggestions || []);
      setSuggestMeta({ source: data.source, basedOn: data.based_on_posts });
      if (!(data.suggestions || []).length) toast.error("No suggestions returned, try again");
    } catch { toast.error("Couldn't fetch topics"); }
    setSuggestBusy(false);
  };

  const generate = async () => {
    if (!topic.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/content/generate", { type: "linkedin_post", topic, tone });
      toast.success("Draft generated");
      if (autoImage && data?.id) {
        try { await api.post(`/content/drafts/${data.id}/image`, { image_guidance: null }); toast.info("Generating a picture…"); } catch { /* non-blocking */ }
      }
      load();
    } catch { toast.error("Generation failed"); }
    setBusy(false);
  };

  const remove = async (id) => {
    await api.delete(`/content/drafts/${id}`);
    load();
  };

  return (
    <DashboardLayout eyebrow="Content Studio" title="LinkedIn AI composer">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="data-card lg:col-span-1 h-fit space-y-4">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Topic / hook</Label>
              <button onClick={suggestTopics} disabled={suggestBusy} data-testid="content-suggest-btn"
                className="text-[0.7rem] inline-flex items-center gap-1 text-signal-500 hover:text-signal-400 disabled:opacity-50">
                <Lightbulb className="w-3 h-3" />{suggestBusy ? "Scanning…" : "Suggest topics"}
              </button>
            </div>
            <Input data-testid="content-topic-input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Your post topic" />
            {suggestMeta && (
              <p className="text-[0.65rem] text-muted-foreground mt-1.5">
                {suggestMeta.source === "linkedin_trending"
                  ? `Based on ${suggestMeta.basedOn} trending logistics posts we follow`
                  : "Evergreen ideas (connect LinkedIn + scan Page Engagement for live trends)"}
              </p>
            )}
            {suggestions.length > 0 && (
              <div className="mt-2 space-y-1.5" data-testid="content-suggestions">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => { setTopic(s.topic); toast.success("Topic set"); }}
                    data-testid={`content-suggestion-${i}`}
                    className="w-full text-left p-2 rounded-sm border border-border hover:border-signal-500/50 hover:bg-card/60 transition-colors">
                    <div className="text-xs font-medium leading-snug">{s.topic}</div>
                    {s.angle && <div className="text-[0.65rem] text-muted-foreground mt-0.5">{s.angle}</div>}
                    {s.why_now && <div className="text-[0.6rem] text-signal-500/80 uppercase tracking-wider mt-1">{s.why_now}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Tone</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {TONES.map((t) => (
                <button key={t} onClick={() => setTone(t)} data-testid={`content-tone-${t}`}
                  className={`px-2 py-1.5 rounded-sm text-xs border transition-colors ${
                    tone === t ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"
                  }`}>
                  {t.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>
          <Button onClick={generate} disabled={busy} data-testid="content-generate-btn" className="w-full rounded-sm">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />{busy ? "Composing..." : "Generate post"}
          </Button>
          <button onClick={() => setAutoImage((v) => !v)} data-testid="content-autoimage-toggle"
            className="flex items-center justify-between w-full text-xs text-muted-foreground hover:text-foreground transition-colors">
            <span className="inline-flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> Auto-generate a picture</span>
            <span className={`relative w-9 h-5 rounded-full transition-colors ${autoImage ? "bg-signal-500" : "bg-border"}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${autoImage ? "translate-x-4" : ""}`} />
            </span>
          </button>
          <p className="text-xs text-muted-foreground">Logistics-tuned voice. Avoids hashtag spam and generic AI fluff.</p>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {drafts.length === 0 && (
            <div className="data-card text-sm text-muted-foreground text-center py-12">No drafts yet — compose your first post.</div>
          )}
          {drafts.map((d, i) => (
            <DraftCard key={d.id} d={d} i={i} onRemove={remove} onChanged={load} />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

function DraftCard({ d, i, onRemove, onChanged }) {
  const [img, setImg] = useState(d.image_b64 || null);
  const [imgStatus, setImgStatus] = useState(d.image_status || null);
  const [guidance, setGuidance] = useState(d.image_guidance || "");
  const [showGuidance, setShowGuidance] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirmId, setConfirmId] = useState(null); // "page" | "personal"
  const [body, setBody] = useState(d.body);
  const [regen, setRegen] = useState(false);
  const [approved, setApproved] = useState(!!d.approved);
  const published = d.status === "published";

  const toggleApprove = async () => {
    try {
      const { data } = await api.post(`/content/drafts/${d.id}/approve`);
      setApproved(data.approved);
      toast.success(data.approved ? "Approved for deployment" : "Approval removed");
    } catch { toast.error("Couldn't update approval"); }
  };

  // Load existing image once if the draft already has one.
  useEffect(() => {
    if (d.image_status === "ready" && !img) {
      api.get(`/content/drafts/${d.id}/image`).then((r) => setImg(r.data.image_b64)).catch(() => {});
    } else if (d.image_status === "generating") {
      pollImage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollImage = () => {
    setImgStatus("generating");
    const poll = setInterval(async () => {
      try {
        const { data } = await api.get(`/content/drafts/${d.id}/image`);
        if (data.image_status === "ready") { setImg(data.image_b64); setImgStatus("ready"); clearInterval(poll); }
        else if (data.image_status === "failed") { setImgStatus("failed"); clearInterval(poll); toast.error("Image generation failed"); }
      } catch { clearInterval(poll); }
    }, 4000);
    setTimeout(() => clearInterval(poll), 90000);
  };

  const regenerateText = async () => {
    setRegen(true);
    try {
      const { data } = await api.post(`/content/drafts/${d.id}/regenerate`);
      setBody(data.body); toast.success("Post rewritten");
    } catch { toast.error("Couldn't regenerate"); }
    setRegen(false);
  };

  const genImage = async () => {
    setShowGuidance(false);
    setImgStatus("generating");
    try {
      await api.post(`/content/drafts/${d.id}/image`, { image_guidance: guidance || null });
      pollImage();
    } catch { setImgStatus("failed"); toast.error("Couldn't start image"); }
  };

  const publish = async (identity) => {
    setPublishing(true); setConfirmId(null);
    try {
      const { data } = await api.post(`/content/drafts/${d.id}/publish`, { identity });
      if (data.ok) { toast.success(`Posted to LinkedIn ${identity === "page" ? "as Europacific" : "as Rok"}`); onChanged(); }
      else {
        const msg = { no_connected_linkedin_account: "Connect LinkedIn first (Integrations).",
          no_company_page_configured: "Configure the Europacific company page in Page Engagement settings." }[data.error] || data.error;
        toast.error(`Publish failed: ${msg}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    setPublishing(false);
  };

  return (
    <div className="data-card" data-testid={`content-draft-${i}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="min-w-0">
          <div className="label-eyebrow">{d.tone?.replace(/_/g, " ")} · {d.type?.replace(/_/g, " ")}{published && <span className="text-signal-500 ml-2">· published {d.posted_as === "page" ? "as page" : "as Rok"}</span>}{!published && approved && <span className="text-emerald-400 ml-2" data-testid={`content-approved-badge-${i}`}>· approved for deployment</span>}{d.auto_generated && !published && <span className="text-muted-foreground ml-2">· AI-drafted</span>}</div>
          <div className="text-sm font-medium mt-1 truncate">{d.topic}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!published && (
            <button onClick={regenerateText} disabled={regen} title="Regenerate post text" data-testid={`content-regenerate-${i}`}
              className="w-8 h-8 flex items-center justify-center border border-border rounded-sm hover:border-foreground/30 disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${regen ? "animate-spin" : ""}`} />
            </button>
          )}
          <button onClick={() => { navigator.clipboard.writeText(body); toast.success("Copied"); }} className="w-8 h-8 flex items-center justify-center border border-border rounded-sm hover:border-foreground/30" data-testid={`content-copy-${i}`}>
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onRemove(d.id)} className="w-8 h-8 flex items-center justify-center border border-border rounded-sm hover:border-destructive/40 hover:text-destructive" data-testid={`content-delete-${i}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {img && <img src={`data:image/png;base64,${img}`} alt="post" className="w-full rounded-sm border border-border mb-3 object-cover" data-testid={`content-image-${i}`} />}

      <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed border-t border-border pt-3">{body}</pre>

      {!published && (
        <div className="mt-4 border-t border-border pt-3 space-y-3">
          {/* Image controls */}
          {showGuidance ? (
            <div className="flex items-center gap-2">
              <Input value={guidance} onChange={(e) => setGuidance(e.target.value)} placeholder="Describe the picture (optional)" data-testid={`content-image-guidance-${i}`} className="h-9 text-xs" />
              <Button onClick={genImage} className="rounded-sm h-9 text-xs" data-testid={`content-image-confirm-${i}`}>Generate</Button>
            </div>
          ) : (
            <button onClick={() => setShowGuidance(true)} disabled={imgStatus === "generating"} data-testid={`content-add-image-${i}`}
              className="text-xs inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground disabled:opacity-50">
              <ImageIcon className="w-3.5 h-3.5" />
              {imgStatus === "generating" ? "Generating picture…" : img ? "Regenerate picture" : "Add a picture"}
            </button>
          )}

          {/* Publish controls */}
          {confirmId ? (
            <div className="flex items-center gap-2 p-2 rounded-sm border border-signal-500/40 bg-signal-500/5">
              <span className="text-xs flex-1">Post {confirmId === "page" ? "as the Europacific page" : "as Rok Kobal (mentioning Europacific)"}{img ? " with the picture" : ""}?</span>
              <Button onClick={() => publish(confirmId)} disabled={publishing} className="rounded-sm h-8 text-xs" data-testid={`content-publish-confirm-${i}`}>{publishing ? "Posting…" : "Confirm"}</Button>
              <Button onClick={() => setConfirmId(null)} variant="outline" className="rounded-sm h-8 text-xs">Cancel</Button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button onClick={toggleApprove} data-testid={`content-approve-${i}`}
                className={`px-3 py-1.5 rounded-sm text-xs border inline-flex items-center gap-1.5 ${approved ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/10" : "border-border hover:border-emerald-500/40 hover:text-emerald-400"}`}>
                <CheckCircle2 className="w-3.5 h-3.5" /> {approved ? "Approved ✓" : "Approve for deployment"}
              </button>
              <button onClick={() => setConfirmId("personal")} data-testid={`content-publish-personal-${i}`}
                className="px-3 py-1.5 rounded-sm text-xs border border-border hover:border-foreground/40 inline-flex items-center gap-1.5">
                <Linkedin className="w-3.5 h-3.5" /> Post as Rok (mention Europacific)
              </button>
              <button onClick={() => setConfirmId("page")} data-testid={`content-publish-page-${i}`}
                className="px-3 py-1.5 rounded-sm text-xs border border-signal-500/40 text-signal-500 hover:bg-signal-500/10 inline-flex items-center gap-1.5">
                <Linkedin className="w-3.5 h-3.5" /> Post as Europacific page
              </button>
            </div>
          )}
        </div>
      )}

      <div className="text-[0.65rem] text-muted-foreground uppercase tracking-wider mt-3">{relativeTime(d.created_at)}</div>
    </div>
  );
}
